import React, { useState, useEffect } from 'react';
import { Clock, Bell, Play, Pause, RotateCcw, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface SafetyTimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  missionNumber?: string;
}

type ReminderInterval = 10 | 15 | 30;

const SafetyTimerModal: React.FC<SafetyTimerModalProps> = ({
  isOpen,
  onClose,
  missionNumber,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [reminderInterval, setReminderInterval] = useState<ReminderInterval>(15);
  const [lastReminderAt, setLastReminderAt] = useState<number>(0);
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isRunning) {
      interval = setInterval(() => {
        setElapsedSeconds((prev) => {
          const newTime = prev + 1;
          checkReminder(newTime);
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, reminderInterval, lastReminderAt]);

  const checkReminder = (currentSeconds: number) => {
    const reminderSeconds = reminderInterval * 60;
    const timeSinceLastReminder = currentSeconds - lastReminderAt;

    if (timeSinceLastReminder >= reminderSeconds) {
      triggerReminder(currentSeconds);
      setLastReminderAt(currentSeconds);
    }
  };

  const triggerReminder = (currentSeconds: number) => {
    // Play audio notification
    playNotificationSound();

    // Show toast notification
    const minutes = Math.floor(currentSeconds / 60);
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-yellow-500 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <Bell className="h-8 w-8 text-white" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-white">
                  Safety Check-in Reminder
                </p>
                <p className="mt-1 text-sm text-white">
                  {minutes} minutes elapsed
                  {missionNumber && ` - Mission ${missionNumber}`}
                </p>
                <p className="mt-1 text-xs text-white opacity-90">
                  Time to check in with the crew
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-white border-opacity-20">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-white hover:bg-yellow-600 focus:outline-none"
            >
              Close
            </button>
          </div>
        </div>
      ),
      {
        duration: 10000,
        position: 'top-center',
      }
    );

    // Browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Safety Check-in Reminder', {
        body: `${minutes} minutes elapsed${missionNumber ? ` - Mission ${missionNumber}` : ''}`,
        icon: '/favicon.ico',
        tag: 'safety-timer',
        requireInteraction: true,
      });
    }
  };

  const playNotificationSound = () => {
    // Create a simple beep using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('Notifications enabled');
      } else {
        toast.error('Notifications denied');
      }
    }
  };

  const handleStartPause = () => {
    if (!isRunning) {
      requestNotificationPermission();
    }
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setElapsedSeconds(0);
    setLastReminderAt(0);
    setIsRunning(false);
  };

  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getNextReminderIn = (): string => {
    const reminderSeconds = reminderInterval * 60;
    const timeSinceLastReminder = elapsedSeconds - lastReminderAt;
    const timeUntilNext = reminderSeconds - timeSinceLastReminder;

    if (timeUntilNext <= 0) return '00:00';

    const minutes = Math.floor(timeUntilNext / 60);
    const seconds = timeUntilNext % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Clock className="h-6 w-6 text-indigo-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Safety Check-in Timer</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Timer Display */}
        <div className="p-6">
          {missionNumber && (
            <div className="text-center mb-4">
              <span className="inline-block bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                Mission {missionNumber}
              </span>
            </div>
          )}

          {/* Main Timer */}
          <div className="text-center mb-6">
            <div className="text-6xl font-mono font-bold text-gray-900 mb-2">
              {formatTime(elapsedSeconds)}
            </div>
            <p className="text-sm text-gray-500">
              {isRunning ? 'Timer running...' : 'Timer paused'}
            </p>
          </div>

          {/* Reminder Interval Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-in Reminder Interval
            </label>
            <div className="grid grid-cols-3 gap-2">
              {([10, 15, 30] as ReminderInterval[]).map((interval) => (
                <button
                  key={interval}
                  onClick={() => setReminderInterval(interval)}
                  disabled={isRunning}
                  className={`py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    reminderInterval === interval
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {interval} min
                </button>
              ))}
            </div>
          </div>

          {/* Next Reminder Countdown */}
          {isRunning && (
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Bell className="h-4 w-4 text-yellow-600 mr-2" />
                  <span className="text-sm font-medium text-yellow-900">
                    Next reminder in:
                  </span>
                </div>
                <span className="text-lg font-mono font-bold text-yellow-900">
                  {getNextReminderIn()}
                </span>
              </div>
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleStartPause}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-md text-white font-medium ${
                isRunning
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isRunning ? (
                <>
                  <Pause className="h-5 w-5 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Start
                </>
              )}
            </button>
            <button
              onClick={handleReset}
              className="flex items-center justify-center px-4 py-3 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-medium"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              Reset
            </button>
          </div>

          {/* Info */}
          <div className="mt-4 text-xs text-gray-500 space-y-1">
            <p>• Audio and visual reminders will trigger at set intervals</p>
            <p>• Browser notifications require permission (granted on start)</p>
            <p>• Timer continues running in the background</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyTimerModal;
